// components/MatchSuggestionPopup.js
import React from 'react';
import { Play, Clock, X } from 'lucide-react';

const MatchSuggestionPopup = ({ 
  suggestedGame, 
  courtId, 
  onAccept, 
  onWait, 
  onClose,
  availableGamesCount = 0,
  getTeamName = null
}) => {
  if (!suggestedGame) return null;

  const handleAccept = () => {
    onAccept(suggestedGame, courtId);
  };

  const handleWait = () => {
    onWait();
  };

  const getGameTypeDisplay = (gameType) => {
    const gameTypes = {
      'MM': "Men's Doubles",
      'FF': "Women's Doubles", 
      'MX1': 'Mixed Doubles (M1+F1)',
      'MX2': 'Mixed Doubles (M2+F2)'
    };
    return gameTypes[gameType] || suggestedGame.gameTypeLabel || gameType;
  };

  // UPDATED: Use team names if available
  const getTeamDisplayName = (teamId) => {
    if (getTeamName) {
      return getTeamName(teamId);
    }
    return `Team ${teamId}`;
  };

  // UPDATED: Generate match title in new format "Tigers vs Dolphins: Mens Doubles"
  const getMatchTitle = () => {
    const team1Name = getTeamDisplayName(suggestedGame.team1);
    const team2Name = getTeamDisplayName(suggestedGame.team2);
    const gameTypeLabel = getGameTypeDisplay(suggestedGame.gameType);
    
    return `${team1Name} vs ${team2Name}: ${gameTypeLabel}`;
  };

  // Extract player names from the game data (already converted to display names)
  const getPlayersForTeam = (teamPlayers) => {
    if (!teamPlayers || !Array.isArray(teamPlayers)) return [];
    
    return teamPlayers.map(player => {
      if (typeof player === 'string') {
        return player; // Already display name format
      }
      return player.name || player;
    });
  };

  const team1Players = getPlayersForTeam(suggestedGame.players?.team1);
  const team2Players = getPlayersForTeam(suggestedGame.players?.team2);

  return (
    <div className="match-suggestion-overlay">
      <div className="match-suggestion-popup">
        {/* Header */}
        <div className="popup-header">
          <div className="header-content">
            <h3>Next Match Available</h3>
            <p>Court {courtId} is ready for a new game</p>
          </div>
          <button className="close-button" onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        {/* Game Details */}
        <div className="game-details">
          <div className="game-type">
            <div className="game-type-badge">
              {getGameTypeDisplay(suggestedGame.gameType)}
            </div>
            <div className="match-id">
              Game {suggestedGame.id}: {getMatchTitle()}
            </div>
          </div>

          {/* Teams */}
          <div className="teams-display">
            <div className="team team-1">
              <div className="team-header">
                <div className="team-name">{getTeamDisplayName(suggestedGame.team1)}</div>
                <div className="team-id">{suggestedGame.team1}</div>
              </div>
              <div className="team-players">
                {team1Players.map((player, index) => (
                  <div key={index} className="player-card">
                    <div className="player-name">{player}</div>
                  </div>
                ))}
              </div>
            </div>

            <div className="vs-divider">
              <span>VS</span>
            </div>

            <div className="team team-2">
              <div className="team-header">
                <div className="team-name">{getTeamDisplayName(suggestedGame.team2)}</div>
                <div className="team-id">{suggestedGame.team2}</div>
              </div>
              <div className="team-players">
                {team2Players.map((player, index) => (
                  <div key={index} className="player-card">
                    <div className="player-name">{player}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Priority Score Info */}
        {suggestedGame.priorityScore && (
          <div className="additional-info">
            <div className="info-item">
              <span className="info-label">Optimization Score:</span>
              <span className="info-value">{Math.round(suggestedGame.priorityScore)} points</span>
            </div>
            {availableGamesCount > 1 && (
              <div className="info-item">
                <span className="info-label">Alternative games available:</span>
                <span className="info-value">{availableGamesCount - 1} other options</span>
              </div>
            )}
          </div>
        )}

        {/* Actions */}
        <div className="popup-actions">
          <button 
            className="action-button wait-button"
            onClick={handleWait}
          >
            <Clock size={18} />
            Wait for Different Match
          </button>
          
          <button 
            className="action-button start-button"
            onClick={handleAccept}
          >
            <Play size={18} />
            Start This Match
          </button>
        </div>

        {/* Footer */}
        <div className="popup-footer">
          <p>This match was selected using dynamic optimization for maximum tournament efficiency</p>
        </div>
      </div>

      <style jsx>{`
        .match-suggestion-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.7);
          backdrop-filter: blur(8px);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
          padding: 20px;
        }

        .match-suggestion-popup {
          background: linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(255, 255, 255, 0.9) 100%);
          backdrop-filter: blur(20px);
          border: 2px solid rgba(255, 255, 255, 0.3);
          border-radius: 20px;
          max-width: 600px;
          width: 100%;
          max-height: 90vh;
          overflow-y: auto;
          color: #1e3a8a;
          box-shadow: 0 25px 50px rgba(0, 0, 0, 0.3);
        }

        .popup-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          padding: 24px 24px 0 24px;
          border-bottom: 1px solid rgba(30, 58, 138, 0.1);
          margin-bottom: 24px;
        }

        .header-content h3 {
          font-size: 1.5rem;
          font-weight: bold;
          color: #1e3a8a;
          margin: 0 0 4px 0;
        }

        .header-content p {
          color: rgba(30, 58, 138, 0.7);
          margin: 0;
          font-size: 0.95rem;
        }

        .close-button {
          background: none;
          border: none;
          color: rgba(30, 58, 138, 0.6);
          cursor: pointer;
          padding: 4px;
          border-radius: 4px;
          transition: all 0.2s;
        }

        .close-button:hover {
          background: rgba(30, 58, 138, 0.1);
          color: #1e3a8a;
        }

        .game-details {
          padding: 0 24px 24px 24px;
        }

        .game-type {
          text-align: center;
          margin-bottom: 24px;
        }

        .game-type-badge {
          display: inline-block;
          background: linear-gradient(135deg, #deff01 0%, #bef264 100%);
          color: #1e3a8a;
          padding: 8px 20px;
          border-radius: 20px;
          font-weight: bold;
          font-size: 1.1rem;
          margin-bottom: 8px;
        }

        .match-id {
          font-size: 0.9rem;
          color: rgba(30, 58, 138, 0.7);
          font-weight: 500;
        }

        .teams-display {
          display: grid;
          grid-template-columns: 1fr auto 1fr;
          gap: 20px;
          align-items: center;
        }

        .team {
          background: rgba(30, 58, 138, 0.05);
          border: 2px solid rgba(30, 58, 138, 0.1);
          border-radius: 12px;
          padding: 16px;
        }

        .team-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 12px;
          padding-bottom: 8px;
          border-bottom: 1px solid rgba(30, 58, 138, 0.1);
        }

        .team-name {
          font-weight: bold;
          color: #1e3a8a;
          font-size: 1.1rem;
        }

        .team-id {
          background: #1e3a8a;
          color: white;
          padding: 4px 8px;
          border-radius: 6px;
          font-size: 0.8rem;
          font-weight: bold;
        }

        .team-players {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .player-card {
          display: flex;
          justify-content: center;
          align-items: center;
          background: rgba(255, 255, 255, 0.6);
          padding: 8px 12px;
          border-radius: 8px;
          border: 1px solid rgba(30, 58, 138, 0.1);
        }

        .player-name {
          font-weight: 500;
          color: #1e3a8a;
          text-align: center;
        }

        .vs-divider {
          display: flex;
          align-items: center;
          justify-content: center;
          background: linear-gradient(135deg, #deff01 0%, #bef264 100%);
          color: #1e3a8a;
          width: 60px;
          height: 60px;
          border-radius: 50%;
          font-weight: bold;
          font-size: 1.2rem;
          border: 3px solid white;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
        }

        .additional-info {
          padding: 0 24px 16px 24px;
          border-top: 1px solid rgba(30, 58, 138, 0.1);
          margin-top: 16px;
          padding-top: 16px;
        }

        .info-item {
          display: flex;
          justify-content: space-between;
          align-items: center;
          font-size: 0.9rem;
          margin-bottom: 8px;
        }

        .info-label {
          color: rgba(30, 58, 138, 0.7);
        }

        .info-value {
          color: #1e3a8a;
          font-weight: 500;
        }

        .popup-actions {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 12px;
          padding: 0 24px 24px 24px;
        }

        .action-button {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          padding: 12px 20px;
          border: none;
          border-radius: 10px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s;
          font-size: 0.95rem;
        }

        .wait-button {
          background: rgba(30, 58, 138, 0.1);
          color: #1e3a8a;
          border: 2px solid rgba(30, 58, 138, 0.2);
        }

        .wait-button:hover {
          background: rgba(30, 58, 138, 0.15);
          border-color: rgba(30, 58, 138, 0.3);
          transform: translateY(-1px);
        }

        .start-button {
          background: linear-gradient(135deg, #10b981 0%, #059669 100%);
          color: white;
          border: 2px solid transparent;
        }

        .start-button:hover {
          background: linear-gradient(135deg, #059669 0%, #047857 100%);
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(16, 185, 129, 0.3);
        }

        .popup-footer {
          text-align: center;
          padding: 16px 24px;
          background: rgba(30, 58, 138, 0.05);
          border-top: 1px solid rgba(30, 58, 138, 0.1);
          border-bottom-left-radius: 18px;
          border-bottom-right-radius: 18px;
        }

        .popup-footer p {
          margin: 0;
          font-size: 0.85rem;
          color: rgba(30, 58, 138, 0.6);
          font-style: italic;
        }

        @media (max-width: 640px) {
          .match-suggestion-popup {
            margin: 10px;
            border-radius: 16px;
          }

          .teams-display {
            grid-template-columns: 1fr;
            gap: 16px;
          }

          .vs-divider {
            width: 50px;
            height: 50px;
            font-size: 1rem;
            justify-self: center;
          }

          .popup-actions {
            grid-template-columns: 1fr;
          }

          .player-card {
            justify-content: center;
          }
        }
      `}</style>
    </div>
  );
};

export default MatchSuggestionPopup;